import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useState, useRef } from 'react';
import { FAIcon, GenericLink, useTranslation, Breakpoints, useBreakpoint } from '@nextgen-web-framework/all';
import styles from './GoogleMaps.module.scss';
import type { GoogleMapProps, StoreLocation } from './types/propsTypes';

const GoogleMaps = (googleMapProps: GoogleMapProps) => {
  const {
    mapZoomLevel = 10,
    customClass,
    selectedStoreId,
    getUpdatedStoreId,
    storeLocations,
    onConfirm,
    showConfirmLink,
    customMarkerURL,
  } = googleMapProps;

  const config = ConfigService.getConfigs();
  const googleAppKey = config.GOOGLE_MAPS_API_KEY ?? config.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const isDetailPopupRequiredRef = useRef(true);
  const lastSelectedStoreIdRef = useRef('');
  const t = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: `${googleAppKey}`,
  });

  const mapStyles = { height: '100%', width: '100%' };

  const [selectedStore, setSelectedStore] = useState<StoreLocation | null>(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState<google.maps.LatLngLiteral | null>(null);

  const mapRef = useRef<google.maps.Map | null>(null);
  const viewPortType = useBreakpoint() as Breakpoints;
  const isMobileView = viewPortType == Breakpoints.XS || viewPortType == Breakpoints.SM;
  const getSelectedStore = (storeLocations: StoreLocation[]) => {
    setSelectedStore(storeLocations.find((store) => store.id === selectedStoreId) ?? null);
  };

  useEffect(() => {
    getSelectedStore(storeLocations);
    isDetailPopupRequiredRef.current = true;
  }, [selectedStoreId, storeLocations]);

  useEffect(() => {
    lastSelectedStoreIdRef.current = selectedStore?.id ?? '';
  }, [selectedStore]);

  const handleMarkerClick = (location: StoreLocation) => {
    setSelectedStore(location);
    if (lastSelectedStoreIdRef.current === location?.id) {
      setInfoWindowPosition({ lat: location.lat, lng: location.lng });
    }
    lastSelectedStoreIdRef.current = location.id;
    if (typeof getUpdatedStoreId === 'function') {
      getUpdatedStoreId(location.id);
    }
  };

  const handleConfirmClick = () => {
    if (typeof onConfirm === 'function') {
      onConfirm(selectedStore);
    }
  };

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    map.addListener('zoom_changed', () => {
      isDetailPopupRequiredRef.current = false;
      setInfoWindowPosition(null);
    });
    map.addListener('idle', () => {
      const center = map.getCenter();
      if (center && isDetailPopupRequiredRef.current) {
        const { lat, lng } = center.toJSON();
        setInfoWindowPosition({ lat, lng });
      }
      isDetailPopupRequiredRef.current = false;
    });
  };
  const StoreDirections = (selectedStore: StoreLocation) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${selectedStore?.lat},${selectedStore?.lng}`;

    return (
      <GenericLink target="_blank" href={googleMapsUrl}>
        <div className={styles['store-direction-style']}>
          {t('web.shopFinder.getDirections')}
          <FAIcon
            iconName="external-link"
            className={styles['external-style-icon']}
            data-testid="back-button-test-id"
          />
        </div>
      </GenericLink>
    );
  };

  return (
    <div className={customClass ?? styles['google-map-container']} data-testid={'google-map'}>
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={mapStyles}
          zoom={mapZoomLevel}
          center={selectedStore || { lat: 0, lng: 0 }}
          onLoad={handleMapLoad}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: !isMobileView,
          }}
        >
          {storeLocations.map((location) => (
            <Marker
              key={location.id}
              position={location}
              icon={
                customMarkerURL
                  ? {
                      scaledSize:
                        location.id === selectedStoreId
                          ? new window.google.maps.Size(
                              customMarkerURL.scaledMarkerSize.selectedMarkerSize.height,
                              customMarkerURL.scaledMarkerSize.selectedMarkerSize.width,
                            )
                          : new window.google.maps.Size(
                              customMarkerURL.scaledMarkerSize.defaultMarkerSize.height,
                              customMarkerURL.scaledMarkerSize.defaultMarkerSize.width,
                            ),
                      url:
                        location.id === selectedStoreId
                          ? customMarkerURL.selectedMarkerURL
                          : customMarkerURL.defaultCustomMarkerURL,
                    }
                  : undefined
              }
              onClick={() => handleMarkerClick(location)}
            />
          ))}
          {infoWindowPosition && selectedStore?.storeName && selectedStore?.storeAddress && (
            <InfoWindow position={infoWindowPosition} options={{ pixelOffset: new google.maps.Size(0, -40) }}>
              <div className={styles['google-map-address-popup']}>
                <div className={styles['store-heading-style']}>
                  <span className={styles['store-name-style']}>{selectedStore.storeName}</span>
                </div>
                <p className={styles['store-address-style']}>{selectedStore.storeAddress}</p>
                <div className={styles['store-footer-container']}>
                  <div className={styles['directions-wrapper']}>{StoreDirections(selectedStore)}</div>
                  {showConfirmLink && (
                    <div
                      role="button"
                      tabIndex={0}
                      data-testid="confirm-button"
                      aria-label="Confirm Link"
                      className={styles['confirm-link']}
                      onClick={handleConfirmClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleConfirmClick();
                        }
                      }}
                    >
                      {t('web.shopFinder.confirm')}
                    </div>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
};

export default GoogleMaps;
